// src/components/AdminUsers.js
import React, { useState, useEffect } from "react";
import { adminApi } from "../../services/adminApi";

// Plan IDs - Standard (free) plan assigned to all users by Memberstack
const STANDARD_FREE_PLAN_ID = 'pln_free-plan-kwo20e9r';

/**
 * Derive a human-readable subscription status from a member's planConnections
 */
function deriveStatus(planConnections = []) {
  if (!planConnections.length) return "none";

  // Check for standard (free) plan first
  const standardFreePlan = planConnections.find(
    (p) => p.planId === STANDARD_FREE_PLAN_ID &&
           (p.status || "").toLowerCase() === "active"
  );
  if (standardFreePlan) return "standard (free)";

  // Check for paid subscriptions
  const subs = planConnections.filter(
    (p) => (p.type || "").toLowerCase() === "subscription"
  );
  if (!subs.length) return "none";

  const activeOrTrial = subs.find((p) => {
    const s = (p.status || "").toLowerCase();
    return s === "active" || s === "trialing";
  });
  if (activeOrTrial) {
    return activeOrTrial.status.toLowerCase() === "trialing"
      ? "trialing"
      : "active";
  }

  const cancelled = subs.find((p) => {
    const s = (p.status || "").toLowerCase();
    return s === "cancelled" || s === "canceled";
  });
  return cancelled ? "cancelled" : "none";
}


// Active is the one worth calling out - bright green. Cancelled is the one
// worth an admin's attention - bright red. No-subscription users are
// neutral (never had one to chase up), so they get a plain grey rather than
// being lumped in with Active. Trialing/standard (free) are left
// unhighlighted since they're neither.
function statusHighlightStyle(status) {
  const s = (status || "").toLowerCase();
  if (s === "active") return { backgroundColor: "#A8E6B2" };
  if (s === "cancelled" || s === "canceled") return { backgroundColor: "#EFAFA9" };
  if (s === "none") return { backgroundColor: "#D6D6D6" };
  return null;
}

const STATUS_ORDER = ["active", "trialing", "standard (free)", "cancelled", "none"];
const STATUS_LABELS = {
  // Memberstack's own plan status stays "active" right up until a
  // scheduled cancellation actually lapses, so this bucket already
  // includes anyone due to cancel - the label just makes that explicit
  // rather than implying they've been excluded.
  active: "Active (includes ones due to cancel)",
  trialing: "Trialing",
  "standard (free)": "Standard (Free)",
  cancelled: "Cancelled",
  none: "None",
};

function statsChipStyle(status) {
  return statusHighlightStyle(status) || { backgroundColor: "#f0f0f0" };
}

function AdminUsers({ adminToken, tabName }) {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // filters
  const [marketingFilter, setMarketingFilter] = useState("");
  const [subscriptionFilter, setSubscriptionFilter] = useState("");
  const [minBookings, setMinBookings] = useState("");
  const [maxBookings, setMaxBookings] = useState("");
  const [createdFromFilter, setCreatedFromFilter] = useState("");
  const [createdToFilter, setCreatedToFilter] = useState("");

  // sorting
  const [sortColumn, setSortColumn] = useState(null); // column name to sort by
  const [sortDirection, setSortDirection] = useState("asc"); // "asc" or "desc"

  useEffect(() => {
    if (adminToken) fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken]);

  /**
   * Fetch users + their plan data in two *bulk* calls
   *   1. /admin/users               → internal profile data
   *   2. /admin/members-with-plans  → Memberstack data (batched)
   * Then sort everything by created_at DESC (newest first).
   */
  async function fetchUsers() {
    try {
      setLoading(true);
      setError("");

      /* 1️⃣ internal users ---------------------------------------------- */
      const raw = await adminApi.getUsers(adminToken);
      const baseUsers = Array.isArray(raw) ? raw : raw?.data || [];

      if (baseUsers.length === 0) {
        setUsers([]);
        return;
      }

      /* 2️⃣ batched Memberstack lookup ---------------------------------- */
      const memberIds = baseUsers
        .map((u) => u.memberstack_id)
        .filter(Boolean);

      const planLookup = new Map(); // memId → status
      if (memberIds.length) {
        const { data: members } = await adminApi.getMembersWithPlans(adminToken);
        (members || []).forEach((m) =>
          planLookup.set(
            m.id,
            deriveStatus(m.planConnections || [])
          )
        );
      }

      /* 3️⃣ merge + sort by created_at DESC ----------------------------- */
      const combined = baseUsers
        .map((u) => ({
          ...u,
          subscription_status: planLookup.get(u.memberstack_id) || "none",
        }))
        .sort((a, b) => b.created_at.localeCompare(a.created_at));

      setUsers(combined);
    } catch (err) {
      console.error(err);
      setError(err.message || "Error fetching users");
    } finally {
      setLoading(false);
    }
  }


  /* ---------- existing filter / render code remains unchanged ---------- */
  function handleFilterSubmit(e) {
    e.preventDefault();
  }

  function handleSort(column) {
    if (sortColumn === column) {
      // If clicking the same column, toggle direction or clear
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        // Clear sorting
        setSortColumn(null);
        setSortDirection("asc");
      }
    } else {
      // New column - start with ascending
      setSortColumn(column);
      setSortDirection("asc");
    }
  }

  // Helper function to format dates without milliseconds
  function formatDate(dateString) {
    if (!dateString) return "—";
    // Remove milliseconds and 'Z', format as YYYY-MM-DD HH:MM:SS
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  // Helper function to render sortable column header
  function renderSortableHeader(columnKey, label) {
    const isActive = sortColumn === columnKey;
    let sortIndicator;

    if (isActive) {
      // Active sort: show clear up/down arrow
      sortIndicator = sortDirection === "asc" ? " ↑" : " ↓";
    } else {
      // Inactive but sortable: show neutral indicator
      sortIndicator = " ⇅";
    }

    return (
      <th
        style={{
          textAlign: "left",
          padding: "8px",
          cursor: "pointer",
          userSelect: "none",
          fontWeight: isActive ? "bold" : "normal",
          color: isActive ? "#000" : "#333"
        }}
        onClick={() => handleSort(columnKey)}
        title={`Click to sort by ${label}`}
      >
        {label}
        <span style={{
          opacity: isActive ? 1 : 0.4,
          fontSize: isActive ? "1em" : "0.85em",
          marginLeft: "4px"
        }}>
          {sortIndicator}
        </span>
      </th>
    );
  }

  // Every filter except subscription status - the status breakdown stats
  // below are computed from this set (not the fully-filtered one) so they
  // keep showing the full spread across statuses even while the table
  // itself is drilled into a single one via subscriptionFilter.
  const usersBeforeStatusFilter = users.filter((u) => {
    if (marketingFilter === "true" && !u.marketing_opt_in) return false;
    if (marketingFilter === "false" && u.marketing_opt_in) return false;

    const bookingsCount = u.confirmed_bookings_count || 0;
    if (minBookings !== "" && bookingsCount < parseInt(minBookings)) return false;
    if (maxBookings !== "" && bookingsCount > parseInt(maxBookings)) return false;

    if (createdFromFilter && (!u.created_at || new Date(u.created_at) < new Date(`${createdFromFilter}T00:00:00`))) {
      return false;
    }
    if (createdToFilter && (!u.created_at || new Date(u.created_at) > new Date(`${createdToFilter}T23:59:59`))) {
      return false;
    }

    return true;
  });

  let filteredUsers = usersBeforeStatusFilter.filter((u) => {
    const status = (u.subscription_status || "none").toLowerCase();
    if (subscriptionFilter && subscriptionFilter !== status) return false;
    return true;
  });

  // Counts per subscription status for the stats row - see
  // usersBeforeStatusFilter's comment for why subscriptionFilter itself is
  // excluded here.
  const statusCounts = usersBeforeStatusFilter.reduce((acc, u) => {
    const status = (u.subscription_status || "none").toLowerCase();
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  // Apply sorting if a column is selected
  if (sortColumn) {
    filteredUsers = [...filteredUsers].sort((a, b) => {
      let aVal, bVal;

      switch (sortColumn) {
        case "id":
          aVal = a.id;
          bVal = b.id;
          break;
        case "name":
          aVal = `${a.first_name || ""} ${a.last_name || ""}`.toLowerCase();
          bVal = `${b.first_name || ""} ${b.last_name || ""}`.toLowerCase();
          break;
        case "email":
          aVal = (a.email || "").toLowerCase();
          bVal = (b.email || "").toLowerCase();
          break;
        case "age":
          aVal = a.birth_year ? currentYear - a.birth_year : -1;
          bVal = b.birth_year ? currentYear - b.birth_year : -1;
          break;
        case "city":
          aVal = (a.city || "").toLowerCase();
          bVal = (b.city || "").toLowerCase();
          break;
        case "country":
          aVal = (a.country_grew_up_in || "").toLowerCase();
          bVal = (b.country_grew_up_in || "").toLowerCase();
          break;
        case "created_at":
          aVal = a.created_at || "";
          bVal = b.created_at || "";
          break;
        case "updated_at":
          aVal = a.updated_at || "";
          bVal = b.updated_at || "";
          break;
        case "marketing":
          aVal = a.marketing_opt_in ? 1 : 0;
          bVal = b.marketing_opt_in ? 1 : 0;
          break;
        case "subscription":
          aVal = (a.subscription_status || "").toLowerCase();
          bVal = (b.subscription_status || "").toLowerCase();
          break;
        case "bookings":
          aVal = a.confirmed_bookings_count || 0;
          bVal = b.confirmed_bookings_count || 0;
          break;
        case "last_booking":
          aVal = a.last_confirmed_booking_date || "";
          bVal = b.last_confirmed_booking_date || "";
          break;
        default:
          return 0;
      }

      // Handle comparison
      let comparison = 0;
      if (typeof aVal === "number" && typeof bVal === "number") {
        comparison = aVal - bVal;
      } else {
        comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });
  }

  // Calculate average age
  const currentYear = new Date().getFullYear();
  const computeAge = (birthYear) =>
    birthYear ? currentYear - birthYear : "—";

  const usersWithAge = filteredUsers.filter((u) => u.birth_year);
  const averageAge = usersWithAge.length > 0
    ? Math.round(usersWithAge.reduce((sum, u) => sum + (currentYear - u.birth_year), 0) / usersWithAge.length)
    : "N/A";

  /* ---------- JSX identical to the original component ---------- */
  return (
    <div>
      <h3 style={{ textAlign: "center" }}>{tabName}</h3>
      {loading && <p>Loading users...</p>}
      {error && <p style={{ color: "red" }}>{error}</p>}

      {/* Filters */}
      <form
        onSubmit={handleFilterSubmit}
        style={{
          display: "flex",
          gap: "0.5rem",
          alignItems: "center",
          marginBottom: "1rem",
          flexWrap: "wrap",
        }}
      >
        <select
          value={marketingFilter}
          onChange={(e) => setMarketingFilter(e.target.value)}
        >
          <option value="">All Marketing</option>
          <option value="true">Opt-In: Yes</option>
          <option value="false">Opt-In: No</option>
        </select>
        <select
          value={subscriptionFilter}
          onChange={(e) => setSubscriptionFilter(e.target.value)}
        >
          <option value="">All Subscriptions</option>
          <option value="active">Active</option>
          <option value="trialing">Trialing</option>
          <option value="standard (free)">Standard (Free)</option>
          <option value="cancelled">Cancelled</option>
          <option value="none">None</option>
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
          Min Bookings:
          <input
            type="number"
            min="0"
            value={minBookings}
            onChange={(e) => setMinBookings(e.target.value)}
            placeholder="Min"
            style={{ width: "80px" }}
          />
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
          Max Bookings:
          <input
            type="number"
            min="0"
            value={maxBookings}
            onChange={(e) => setMaxBookings(e.target.value)}
            placeholder="Max"
            style={{ width: "80px" }}
          />
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
          Created from
          <input
            type="date"
            value={createdFromFilter}
            onChange={(e) => setCreatedFromFilter(e.target.value)}
          />
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
          Created to
          <input
            type="date"
            value={createdToFilter}
            onChange={(e) => setCreatedToFilter(e.target.value)}
          />
        </label>
        <button type="submit" disabled={loading}>
          Apply Filters
        </button>
      </form>

      {/* Subscription status breakdown */}
      {!loading && (
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "1rem" }}>
          {STATUS_ORDER.map((status) => (
            <div
              key={status}
              style={{
                padding: "8px 14px",
                borderRadius: "8px",
                fontSize: "0.9rem",
                fontWeight: "600",
                ...statsChipStyle(status),
              }}
            >
              {STATUS_LABELS[status]}: {statusCounts[status] || 0}
            </div>
          ))}
        </div>
      )}

      {/* Row count and stats display */}
      {!loading && (
        <p style={{ marginBottom: "1rem", fontWeight: "bold" }}>
          Showing {filteredUsers.length} user{filteredUsers.length !== 1 ? "s" : ""} | Average Age: {averageAge}
        </p>
      )}

      {filteredUsers.length === 0 && !loading && <p>No users found.</p>}

      {filteredUsers.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr
              style={{
                borderBottom: "2px solid #ccc",
                background: "#f0f0f0",
              }}
            >
              {renderSortableHeader("id", "ID")}
              {renderSortableHeader("name", "Name")}
              {renderSortableHeader("email", "Email")}
              {renderSortableHeader("age", "Age")}
              {renderSortableHeader("city", "City")}
              {renderSortableHeader("country", "Country Grew Up In")}
              {renderSortableHeader("created_at", "Created At")}
              {renderSortableHeader("updated_at", "Updated At")}
              {renderSortableHeader("marketing", "Marketing Opt-In")}
              {renderSortableHeader("subscription", "Subscription Status")}
              {renderSortableHeader("bookings", "Confirmed Bookings")}
              {renderSortableHeader("last_booking", "Last Confirmed Booking")}
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((u, idx) => (
              <tr
                key={u.id}
                style={{
                  backgroundColor: idx % 2 === 0 ? "#fff" : "#f9f9f9",
                  borderBottom: "1px solid #eee",
                }}
              >
                <td style={{ padding: "8px" }}>{u.id}</td>
                <td style={{ padding: "8px" }}>
                  {u.first_name} {u.last_name}
                </td>
                <td style={{ padding: "8px" }}>{u.email}</td>
                <td style={{ padding: "8px" }}>{computeAge(u.birth_year)}</td>
                <td style={{ padding: "8px" }}>{u.city || "—"}</td>
                <td style={{ padding: "8px" }}>{u.country_grew_up_in || "—"}</td>
                <td style={{ padding: "8px" }}>{formatDate(u.created_at)}</td>
                <td style={{ padding: "8px" }}>{formatDate(u.updated_at)}</td>
                <td style={{ padding: "8px" }}>
                  {u.marketing_opt_in ? "Yes" : "No"}
                </td>
                <td style={{ padding: "8px", ...statusHighlightStyle(u.subscription_status) }}>{u.subscription_status}</td>
                <td style={{ padding: "8px" }}>{u.confirmed_bookings_count || 0}</td>
                <td style={{ padding: "8px" }}>{formatDate(u.last_confirmed_booking_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default AdminUsers;
